import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Typography,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { round2 } from '../../data/pricingScenarios';

export default function TestScenariosTable({ scenarios, onApplyScenario }) {
  const { t } = useTranslation();

  // Get currency symbol based on region
  const getCurrencySymbol = (region) => {
    return region === 'eu' ? '€' : '$';
  };

  const formatPrice = (price, region = 'ua') => {
    const symbol = getCurrencySymbol(region);
    if (price === 0) return `${symbol}0 (incl.)`;
    return `${symbol}${round2(price).toFixed(2)}`;
  };

  // Calculate subscription prices (divide by bonus multiplier)
  const calculateSubscriptionPrice = (basePrice, days) => {
    if (basePrice === 0) return 0;
    const multiplier = days === 360 ? 1.2 : days === 180 ? 1.1 : 1;
    return basePrice / multiplier;
  };

  const getTierChipColor = (tier) => {
    switch (tier) {
      case 'basic': return 'default';
      case 'pro': return 'primary';
      case 'corporate': return 'secondary';
      case 'advanced': return 'warning';
      default: return 'default';
    }
  };

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.12)' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
              {t('testScenarios.products', 'Products')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">
              {t('testScenarios.tier', 'Tier')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">
              {t('testScenarios.region', 'Region')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              {t('testScenarios.basePrice', 'Base')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              <Tooltip title={t('testScenarios.price360Tooltip', 'Price with 360-day subscription bonus (÷1.2)')}>
                <span>{t('testScenarios.price360', '360d')}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              <Tooltip title={t('testScenarios.price180Tooltip', 'Price with 180-day subscription bonus (÷1.1)')}>
                <span>{t('testScenarios.price180', '180d')}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">
              {t('testScenarios.status', 'Status')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">
              {t('testScenarios.actions', 'Actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scenarios.map((scenario) => {
            const { validation } = scenario;
            const isLoading = validation === null;
            const isSkipped = validation?.skipped;
            const hasError = validation?.error;
            const isPassed = validation?.passed ?? true;
            // Use actual price from API if available, fallback to expected
            const basePrice = validation?.actual ?? scenario.expected.basePrice;
            // Use pre-computed values that correctly split eligible/ineligible products
            const price360 = scenario.expected.price360 ?? calculateSubscriptionPrice(basePrice, 360);
            const price180 = scenario.expected.price180 ?? calculateSubscriptionPrice(basePrice, 180);

            // Row background based on status
            let rowBgColor = 'inherit';
            if (isLoading) rowBgColor = 'grey.50';
            else if (hasError) rowBgColor = 'warning.lighter';
            else if (isSkipped) rowBgColor = 'grey.100';
            else if (!isPassed) rowBgColor = 'error.lighter';

            return (
              <TableRow
                key={scenario.id}
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: rowBgColor,
                  opacity: isLoading ? 0.7 : 1,
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {scenario.productsList?.map((p) => (
                      <Chip
                        key={p.id}
                        label={p.quantity !== null ? `${p.shortName}: ${p.quantity}` : p.shortName}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={scenario.tier.charAt(0).toUpperCase() + scenario.tier.slice(1)}
                    size="small"
                    color={getTierChipColor(scenario.tier)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {scenario.region?.toUpperCase() || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: isPassed ? 'inherit' : 'error.main',
                    }}
                  >
                    {formatPrice(basePrice, scenario.region)}
                  </Typography>
                  {(scenario.expected.frontendOnlyMonthly > 0 || scenario.expected.oneTime > 0) && (
                    <Tooltip
                      title={t(
                        'testScenarios.frontendOnlyTooltip',
                        'Phone numbers are calculated in the frontend calculator, so backend API validation checks only backend-priced products.'
                      )}
                    >
                      <Box sx={{ mt: 0.25 }}>
                        {scenario.expected.frontendOnlyMonthly > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                            {t('testScenarios.backendShort', 'Backend')}: {formatPrice(scenario.expected.backendBasePrice, scenario.region)}
                            {' · '}
                            {t('testScenarios.phonesShort', 'Phones')}: {formatPrice(scenario.expected.frontendOnlyMonthly, scenario.region)}
                          </Typography>
                        )}
                        {scenario.expected.oneTime > 0 && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block', lineHeight: 1.2 }}>
                            {t('testScenarios.oneTimeShort', 'One-time')}: {formatPrice(scenario.expected.oneTime, scenario.region)}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{ color: 'success.main', fontWeight: 500 }}
                  >
                    {formatPrice(price360, scenario.region)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{ color: 'info.main' }}
                  >
                    {formatPrice(price180, scenario.region)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {validation === null ? (
                    <Tooltip title={t('testScenarios.validating', 'Validating...')}>
                      <CircularProgress size={18} />
                    </Tooltip>
                  ) : validation?.skipped ? (
                    <Tooltip title={validation.skipReason || t('testScenarios.skipped', 'Skipped')}>
                      <HelpOutlineIcon color="disabled" fontSize="small" />
                    </Tooltip>
                  ) : validation?.error ? (
                    <Tooltip title={`API Error: ${validation.error}`}>
                      <ErrorIcon color="warning" fontSize="small" />
                    </Tooltip>
                  ) : isPassed ? (
                    <Tooltip title={t('testScenarios.testPassed', 'Test passed (validated against backend API)')}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </Tooltip>
                  ) : (
                    <Tooltip title={`${t('testScenarios.testFailed', 'Test failed')}: expected ${validation?.expected}, got ${validation?.actual}`}>
                      <ErrorIcon color="error" fontSize="small" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => onApplyScenario(scenario)}
                    sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                  >
                    {t('testScenarios.apply', 'Apply')}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {scenarios.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {t('testScenarios.noResults', 'No scenarios match your filters')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
